import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getAllUsers } from "../../redux/actions/user";
import { AiOutlineEye, AiOutlineStop, AiOutlineUndo } from "react-icons/ai";
import { RxCross1 } from "react-icons/rx";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import "./AllUsersForensics.css";
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';


ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const AllUsers = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.user);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'User Sign-ups',
        data: [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      }
    ]
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [quarantineReason, setQuarantineReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [forensicCase, setForensicCase] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [evidenceDraft, setEvidenceDraft] = useState({
    category: "network",
    sourceSystem: "",
    sourceRecordId: "",
    sourceUrl: "",
    title: "",
    observedAt: "",
    summary: "",
    indicatorType: "",
    indicatorValue: "",
    payload: "",
  });
  const [timeFrame, setTimeFrame] = useState('daily'); 

  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch, timeFrame]);

  useEffect(() => {
    if (users && users.length > 0) {
      processChartData(users, timeFrame);
    }
  }, [users, timeFrame]);

  const processChartData = (users, timeFrame) => {
    const sortedUsers = [...users].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const chartLabels = [];
    const signUpCounts = [];
    let currentUserCount = 0;

    sortedUsers.forEach(user => {
      const date = new Date(user.createdAt);
      let label;
      if (timeFrame === 'daily') {
        label = date.toLocaleDateString();
      } else {
        label = `${date.getMonth() + 1}-${date.getFullYear()}`;
      }

      if (!chartLabels.includes(label)) {
        chartLabels.push(label);
        signUpCounts.push(currentUserCount);
        currentUserCount = 0;
      }
      currentUserCount++;
    });
    if (currentUserCount > 0) {
      signUpCounts.push(currentUserCount);
    }

    setChartData({
      labels: chartLabels,
      datasets: [{
        label: 'User Sign-ups',
        data: signUpCounts,
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      }]
    });
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'User Sign-ups Over Time',
        font: {
          size: 24,
        },
        color: '#ffffff',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y;
            return label;
          },
        },
      },
      legend: {
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);
          meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
          chart.update();
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Count',
        },
        beginAtZero: true,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        hoverRadius: 7,
        radius: 5,
      },
    },
  };

  const refreshUsers = async () => {
    await dispatch(getAllUsers());
    setSelectedUser(null);
    setQuarantineReason("");
  };

  const handleQuarantine = async () => {
    try {
      setActionLoading(true);
      const { data } = await axios.post(
        `${server}/user/admin/users/${selectedUser.id}/quarantine`,
        { reason: quarantineReason || "Flagged for investigation" },
        { withCredentials: true }
      );
      toast.success(data.message);
      await refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to quarantine user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRelease = async () => {
    try {
      setActionLoading(true);
      const { data } = await axios.post(
        `${server}/user/admin/users/${selectedUser.id}/release`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message);
      await refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to release user.");
    } finally {
      setActionLoading(false);
    }
  };

  const loadForensics = async (user) => {
    try {
      setSelectedUser(user);
      setEvidenceLoading(true);
      const { data } = await axios.get(
        `${server}/user/admin/users/${user.id}/forensics`,
        { withCredentials: true }
      );
      setForensicCase(data.forensicCase || null);
      setEvidence(data.evidence || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load forensic evidence.");
    } finally {
      setEvidenceLoading(false);
    }
  };

  const handleEvidenceDraftChange = (field, value) => {
    setEvidenceDraft((current) => ({ ...current, [field]: value }));
  };

  const handleAddEvidence = async (event) => {
    event.preventDefault();
    let payload = evidenceDraft.payload;
    if (payload.trim()) {
      try {
        payload = JSON.parse(payload);
      } catch (error) {
        payload = { rawText: payload };
      }
    } else {
      payload = {};
    }

    try {
      setActionLoading(true);
      const indicators = evidenceDraft.indicatorType && evidenceDraft.indicatorValue
        ? [{
            type: evidenceDraft.indicatorType,
            value: evidenceDraft.indicatorValue,
            classification: "observed",
            confidence: "high",
          }]
        : [];
      const { data } = await axios.post(
        `${server}/user/admin/users/${selectedUser.id}/forensics/evidence`,
        {
          ...evidenceDraft,
          observedAt: evidenceDraft.observedAt || undefined,
          indicators,
          payload,
        },
        { withCredentials: true }
      );
      toast.success(data.message);
      setEvidenceDraft({
        category: "network", sourceSystem: "", sourceRecordId: "", sourceUrl: "",
        title: "", observedAt: "", summary: "", indicatorType: "", indicatorValue: "", payload: "",
      });
      await loadForensics(selectedUser);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to preserve evidence.");
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    { field: "id", headerName: "User ID", minWidth: 150, flex: 0.7 },
    {
      field: "avatar",
      headerName: "Avatar",
      renderCell: (params) => (
        <img
          src={params.value}
          alt="avatar"
          className="admin-all-users-avatar"
        />
      ),
      minWidth: 70,
      flex: 0.5,
      sortable: false,
    },

    {
      field: "name",
      headerName: "Name",
      minWidth: 130,
      flex: 0.7,
    },
    {
      field: "email",
      headerName: "Email",
      type: "text",
      minWidth: 130,
      flex: 0.7,
    },
    {
      field: "role",
      headerName: "User Role",
      type: "text",
      minWidth: 130,
      flex: 0.7,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.7,
      renderCell: (params) => (
        <span className={`admin-all-users-status admin-all-users-status--${params.value.toLowerCase()}`}>
          {params.value}
        </span>
      ),
    },
    {
      field: "quarantineId",
      headerName: "Quarantine ID",
      minWidth: 190,
      flex: 1,
    },

    {
      field: "joinedAt",
      headerName: "Joined At",
      type: "text",
      minWidth: 130,
      flex: 0.8,
    },
    {
      field: "viewProfile",
      headerName: "View Profile",
      sortable: false,
      renderCell: (params) => {
        if (!params.value || params.row.isQuarantined) {
          return <span>Unavailable</span>;
        }

        return (
          <Link to={`/user/${params.value}`} className="admin-all-users-view-profile-button">
            View Profile
          </Link>
        );
      },
      minWidth: 150,
      flex: 0.8,
    },

    {
      field: "actions",
      flex: 1,
      minWidth: 110,
      headerName: "Inspect",
      sortable: false,
      renderCell: (params) => {
        return (
          <button
            type="button"
            onClick={() => loadForensics(params.row)}
            className="admin-all-users-delete-button"
            aria-label={`Inspect ${params.row.name}`}
            title="Inspect user"
          >
            <AiOutlineEye size={20} />
          </button>
        );
      },
    },
  ];

  const row = [];
  users &&
    users.forEach((item) => {
      row.push({
        id: item._id,
        name: item.name,
        handle: item.handle,
        viewProfile: item.handle,
        email: item.email,
        role: item.role,
        status: item.quarantine?.isQuarantined ? "Quarantined" : "Active",
        quarantineId: item.quarantine?.caseId || "—",
        isQuarantined: Boolean(item.quarantine?.isQuarantined),
        quarantineReason: item.quarantine?.reason || "",
        quarantinedAt: item.quarantine?.quarantinedAt || null,
        retentionEndsAt: item.quarantine?.retentionEndsAt || null,
        joinedAt: item.createdAt.slice(0, 10),
        avatar: item.avatar?.url,
        account: item,
      });
    });

    return (
      <div className="admin-all-users-container"> 
        <div className="admin-all-users-chart-section"> 
          <div className="admin-all-products-component-timeframe-container">
            <button
              onClick={() => setTimeFrame("daily")}
              className={`admin-all-products-component-timeframe-button ${
                timeFrame === "daily" ? "active" : ""
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setTimeFrame("monthly")}
              className={`admin-all-products-component-timeframe-button ${
                timeFrame === "monthly" ? "active" : ""
              }`}
            >
              Monthly
            </button>
          </div>

          <div className="admin-charts">
            <Line data={chartData} options={options} />
          </div>
        </div>
        <div className="admin-dashboard-orders__section">
          <h2 className="admin-dashboard-orders__title">All Users</h2>
          <div className="admin-dashboard-orders__table-container">
            <table className="admin-dashboard-orders__table">
              <thead className="admin-dashboard-orders__thead">
                <tr>
                  {columns.map((column) => (
                    <th key={column.field} style={{ minWidth: column.minWidth, flex: column.flex }}>
                      {column.headerName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="admin-dashboard-orders__tbody">
                {row.map((user) => (
                  <tr key={user.id} className="admin-dashboard-orders__tr">
                    {columns.map((column) => (
                      <td key={column.field}>
                        {column.renderCell ? column.renderCell({ value: user[column.field], id: user.id, row: user }) : user[column.field]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {selectedUser && (
          <div className="admin-all-users-modal">
            <div className="admin-all-users-modal-content">
              <button
                type="button"
                className="admin-all-users-modal-close"
                onClick={() => {
                  setSelectedUser(null);
                  setForensicCase(null);
                  setEvidence([]);
                }}
                aria-label="Close user inspection"
              >
                <RxCross1 size={25} />
              </button>
              <h3 className="admin-all-users-modal-title">User Investigation</h3>
              <dl className="admin-all-users-inspection">
                <div><dt>Name</dt><dd>{selectedUser.name}</dd></div>
                <div><dt>Handle</dt><dd>@{selectedUser.handle}</dd></div>
                <div><dt>Email</dt><dd>{selectedUser.email}</dd></div>
                <div><dt>Status</dt><dd>{selectedUser.status}</dd></div>
                <div><dt>Account Created</dt><dd>{new Date(selectedUser.account.createdAt).toLocaleString()}</dd></div>
                <div><dt>Last Updated</dt><dd>{new Date(selectedUser.account.updatedAt).toLocaleString()}</dd></div>
                {selectedUser.isQuarantined && (
                  <>
                    <div><dt>Quarantine ID</dt><dd>{selectedUser.quarantineId}</dd></div>
                    <div><dt>Reason</dt><dd>{selectedUser.quarantineReason}</dd></div>
                    <div><dt>Quarantined</dt><dd>{new Date(selectedUser.quarantinedAt).toLocaleString()}</dd></div>
                    <div><dt>Retention Ends</dt><dd>{new Date(selectedUser.retentionEndsAt).toLocaleString()}</dd></div>
                  </>
                )}
              </dl>
              <details className="admin-all-users-evidence-section">
                <summary>Retained Account Record</summary>
                <pre>{JSON.stringify(forensicCase?.subjectSnapshot || selectedUser.account, null, 2)}</pre>
              </details>
              {selectedUser.isQuarantined && (
                <section className="admin-all-users-evidence-section" aria-label="Forensic evidence">
                  <h4>Associated Evidence ({forensicCase?.evidenceCount || evidence.length})</h4>
                  {forensicCase && (
                    <div className="admin-all-users-chain-summary">
                      <span>Forensic hold: {forensicCase.forensicHold ? "Active" : "Released"}</span>
                      <span>Chain head: {forensicCase.chainHeadHash || "Pending"}</span>
                    </div>
                  )}
                  {evidenceLoading && <p>Loading evidence…</p>}
                  {!evidenceLoading && evidence.length === 0 && <p>No associated evidence has been captured yet.</p>}
                  <div className="admin-all-users-evidence-list">
                    {evidence.map((item) => (
                      <details key={item._id} className="admin-all-users-evidence-item">
                        <summary>
                          <span>{item.title}</span>
                          <span>{item.category} · {new Date(item.observedAt || item.capturedAt).toLocaleString()}</span>
                        </summary>
                        <dl>
                          <div><dt>Source</dt><dd>{item.sourceSystem}</dd></div>
                          <div><dt>Source ID</dt><dd>{item.sourceRecordId || "Not supplied"}</dd></div>
                          <div><dt>Observed</dt><dd>{item.observedAt ? new Date(item.observedAt).toLocaleString() : "Not supplied"}</dd></div>
                          <div><dt>Captured</dt><dd>{new Date(item.capturedAt).toLocaleString()}</dd></div>
                          <div><dt>SHA-256</dt><dd>{item.sha256}</dd></div>
                        </dl>
                        {item.summary && <p>{item.summary}</p>}
                        {item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noreferrer">Open source record</a>}
                        {item.indicators?.length > 0 && (
                          <ul>
                            {item.indicators.map((indicator, index) => (
                              <li key={`${indicator.type}-${indicator.value}-${index}`}>
                                {indicator.type}: {indicator.value} ({indicator.classification}, {indicator.confidence})
                              </li>
                            ))}
                          </ul>
                        )}
                        <pre>{JSON.stringify(item.payload, null, 2)}</pre>
                      </details>
                    ))}
                  </div>
                  <details className="admin-all-users-import-evidence">
                    <summary>Add Vercel, GitHub, browser, Tor, socket, or other evidence</summary>
                    <form onSubmit={handleAddEvidence}>
                      <select value={evidenceDraft.category} onChange={(event) => handleEvidenceDraftChange("category", event.target.value)}>
                        {["network", "vercel", "github", "browser", "socket", "request", "database", "other"].map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      <input required placeholder="Source system" value={evidenceDraft.sourceSystem} onChange={(event) => handleEvidenceDraftChange("sourceSystem", event.target.value)} />
                      <input required placeholder="Evidence title" value={evidenceDraft.title} onChange={(event) => handleEvidenceDraftChange("title", event.target.value)} />
                      <input placeholder="Source record ID" value={evidenceDraft.sourceRecordId} onChange={(event) => handleEvidenceDraftChange("sourceRecordId", event.target.value)} />
                      <input type="url" placeholder="Source URL" value={evidenceDraft.sourceUrl} onChange={(event) => handleEvidenceDraftChange("sourceUrl", event.target.value)} />
                      <input type="datetime-local" aria-label="Observed timestamp" value={evidenceDraft.observedAt} onChange={(event) => handleEvidenceDraftChange("observedAt", event.target.value)} />
                      <input placeholder="Indicator type, e.g. tor-exit-node" value={evidenceDraft.indicatorType} onChange={(event) => handleEvidenceDraftChange("indicatorType", event.target.value)} />
                      <input placeholder="Indicator value, e.g. IP address" value={evidenceDraft.indicatorValue} onChange={(event) => handleEvidenceDraftChange("indicatorValue", event.target.value)} />
                      <textarea placeholder="Evidence summary" value={evidenceDraft.summary} onChange={(event) => handleEvidenceDraftChange("summary", event.target.value)} />
                      <textarea placeholder="Raw evidence text or JSON export" value={evidenceDraft.payload} onChange={(event) => handleEvidenceDraftChange("payload", event.target.value)} />
                      <button className="admin-all-users-modal-button" disabled={actionLoading}>Preserve Evidence</button>
                    </form>
                  </details>
                </section>
              )}
              {!selectedUser.isQuarantined && selectedUser.role !== "Admin" && (
                <textarea
                  className="admin-all-users-quarantine-reason"
                  value={quarantineReason}
                  onChange={(event) => setQuarantineReason(event.target.value)}
                  maxLength={500}
                  placeholder="Reason for quarantine"
                  aria-label="Reason for quarantine"
                />
              )}
              <div className="admin-all-users-modal-actions">
                {!selectedUser.isQuarantined && selectedUser.role !== "Admin" && (
                  <button className="admin-all-users-modal-button" onClick={handleQuarantine} disabled={actionLoading}>
                    <AiOutlineStop aria-hidden="true" /> Quarantine
                  </button>
                )}
                {selectedUser.isQuarantined && (
                  <button className="admin-all-users-modal-button" onClick={handleRelease} disabled={actionLoading}>
                    <AiOutlineUndo aria-hidden="true" /> Release
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
};

export default AllUsers;

